import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPaperPlane,
  faPaperclip,
  faSmile
} from '@fortawesome/free-solid-svg-icons';
import chatService from '../services/chatService';
import socketService from '../services/socketService';
import 'react-toastify/dist/ReactToastify.css';

// Add animation styles
const styles = `
  @keyframes messageSlideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Inject styles
const styleSheet = document.createElement('style');
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

const BuddyChat = () => {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const [messages, setMessages] = useState([]);
  const [buddyInfo, setBuddyInfo] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // FIX: setupSocketListeners must only be called after the socket is connected.
  // It is now a plain function (not a useCallback with stale closure issues) called
  // explicitly after connect() resolves.
  const setupSocketListeners = useCallback(() => {
    // Remove old listeners first to prevent duplicates on re-renders / reconnects
    socketService.removeAllListeners('message');
    socketService.removeAllListeners('typing_indicator'); // FIX: was 'typing_start'/'typing_stop'

    // FIX: backend emits the event name 'message' with message.to_dict() directly,
    // not nested under a 'message' key. The old handler tried data.message || data
    // which was fine, but keeping it explicit and safe:
    socketService.on('message', (data) => {
      setMessages(prev => {
        const message = data.message || data;
        // Avoid duplicate messages (e.g. sender receiving their own broadcast)
        if (prev.some(m => m.id === message.id)) {
          return prev;
        }
        return [...prev, message];
      });
    });

    // FIX: backend emits 'typing_indicator' (not 'typing_start' / 'typing_stop')
    // with { match_id, user_id, is_typing: true/false }
    socketService.on('typing_indicator', (data) => {
      if (data.match_id === parseInt(matchId)) {
        setIsTyping(data.is_typing);
      }
    });
  }, [matchId]);

  const loadChatData = useCallback(async () => {
    setLoading(true);
    try {
      const [messagesData, chatInfo] = await Promise.all([
        chatService.getMessages(matchId),
        chatService.getChatInfo(matchId)
      ]);

      setMessages(messagesData.messages || []);

      if (chatInfo && chatInfo.buddy) {
        setBuddyInfo(chatInfo.buddy);
      } else if (chatInfo && chatInfo.other_user) {
        setBuddyInfo(chatInfo.other_user);
      } else if (messagesData.messages && messagesData.messages.length > 0) {
        const firstMessage = messagesData.messages[0];
        if (firstMessage) {
          const senderInfo = firstMessage.sender_name || firstMessage.sender_full_name ||
                           (firstMessage.is_sender ? null : { full_name: firstMessage.sender_name || 'Buddy' });
          if (senderInfo) {
            setBuddyInfo({ full_name: senderInfo.full_name || senderInfo });
          }
        }
      }

      await chatService.markAllAsRead(matchId).catch(() => {});
    } catch (error) {
      console.error('Error loading chat data:', error);
      toast.error('Failed to load chat messages');
      navigate('/buddy');
    } finally {
      setLoading(false);
    }
  }, [matchId, navigate]);

  useEffect(() => {
    loadChatData();

    let isMounted = true;

    const joinAndListen = () => {
      if (!isMounted) return;
      socketService.joinChat(matchId);
      // Re-register listeners each time we (re)join so they are always fresh
      setupSocketListeners();
    };

    const initSocket = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        // No token — nothing real-time, messages only visible after reload
        return;
      }

      // FIX: await the connection promise so that setupSocketListeners is called
      // only after this.socket is set and connected, not before.
      const connected = await socketService.connect(token);
      if (!isMounted) return;

      if (connected) {
        joinAndListen();
      }

      // FIX: register reconnect listener AFTER connect() so socket exists.
      // Use removeAllListeners first to avoid stacking handlers across renders.
      socketService.removeAllListeners('connect');
      socketService.on('connect', joinAndListen);
    };

    initSocket();

    return () => {
      isMounted = false;
      if (matchId) {
        socketService.leaveChat(matchId);
      }
      socketService.removeAllListeners('connect');
      socketService.removeAllListeners('message');
      socketService.removeAllListeners('typing_indicator');
    };
  }, [matchId, loadChatData, setupSocketListeners]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = async () => {
    if (!messageInput.trim()) {
      return;
    }

    const messageContent = messageInput.trim();
    setMessageInput('');
    setSending(true);
    socketService.stopTyping(matchId);

    try {
      const response = await chatService.sendMessage(matchId, messageContent, 'text');
      const persistedMessage = response.data || response;

      if (socketService.isConnected()) {
        // Broadcast to the room — the server will fan the saved message out to
        // all room members (including the web recipient) via the 'message' event.
        socketService.sendMessage(matchId, { message_id: persistedMessage.id });

        // FIX: Optimistically add the sender's own message to local state so it
        // appears immediately on the sender's screen without waiting for the
        // echo from the server. The duplicate-id guard in the listener above
        // will drop the server echo when it arrives.
        setMessages((prev) => {
          if (prev.some(m => m.id === persistedMessage.id)) return prev;
          return [...prev, persistedMessage];
        });
      } else {
        // Socket not available — just show locally
        setMessages((prev) => [...prev, persistedMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      setMessageInput(messageContent); // Restore message
    } finally {
      setSending(false);
    }
  };

  const handleInputChange = (e) => {
    setMessageInput(e.target.value);

    socketService.startTyping(matchId);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socketService.stopTyping(matchId);
    }, 3000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
            <FontAwesomeIcon icon={faPaperPlane} style={{ color: '#FFB6C1' }} spin />
          </div>
          <p>Loading chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      color: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#000'
    }}>
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />

      {/* Header */}
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        padding: '1.5rem 2rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <button
          onClick={() => navigate('/buddy')}
          style={{
            background: 'none',
            border: '1px solid #FFB6C1',
            color: '#FFB6C1',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>

        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: '#FFB6C1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.75rem',
          fontWeight: '700',
          color: '#000'
        }}>
          {buddyInfo?.full_name?.charAt(0)?.toUpperCase() || '?'}
        </div>

        <div style={{ flex: 1 }}>
          <h2 style={{ 
            color: '#FFB6C1', 
            margin: 0,
            marginBottom: '0.25rem',
            fontSize: '1.3rem'
          }}>
            {buddyInfo?.full_name || 'Chat'}
          </h2>
          <p style={{ color: '#999', margin: 0, fontSize: '0.9rem' }}>
            Buddy Chat
          </p>
        </div>
      </div>

      {/* Messages Area */}
      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: '2rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        width: '100%'
      }}>
        {messages.length === 0 ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#666'
          }}>
            <div style={{ textAlign: 'center' }}>
              <FontAwesomeIcon 
                icon={faPaperPlane} 
                style={{ fontSize: '3rem', marginBottom: '1rem', color: '#333' }} 
              />
              <p>No messages yet. Start the conversation!</p>
            </div>
          </div>
        ) : (
          messages.map((message, index) => {
            const isSender = message.is_sender;
            const showDateDivider = index === 0 || 
              formatDate(messages[index - 1].created_at) !== formatDate(message.created_at);

            return (
              <div key={message.id}>
                {showDateDivider && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    margin: '1.5rem 0'
                  }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: '#333' }} />
                    <span style={{ color: '#666', fontSize: '0.85rem', fontWeight: '500' }}>
                      {formatDate(message.created_at)}
                    </span>
                    <div style={{ flex: 1, height: '1px', backgroundColor: '#333' }} />
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: isSender ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-end',
                  gap: '0.75rem',
                  marginBottom: '0.75rem',
                  paddingLeft: isSender ? '0' : '0.5rem',
                  paddingRight: isSender ? '0.5rem' : '0'
                }}>
                  {/* Receiver Avatar */}
                  {!isSender && (
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: '#FFB6C1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1rem',
                      fontWeight: '700',
                      color: '#000',
                      flexShrink: 0
                    }}>
                      {buddyInfo?.full_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}

                  {/* Message Bubble */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isSender ? 'flex-end' : 'flex-start',
                    maxWidth: '65%'
                  }}>
                    {/* Sender/Receiver Name */}
                    {isSender ? (
                      <p style={{
                        margin: '0 0 0.25rem 0',
                        fontSize: '0.8rem',
                        color: '#FFB6C1',
                        fontWeight: '600'
                      }}>
                        You
                      </p>
                    ) : (
                      <p style={{
                        margin: '0 0 0.25rem 0',
                        fontSize: '0.8rem',
                        color: '#FFB6C1',
                        fontWeight: '600'
                      }}>
                        {message.sender?.full_name || buddyInfo?.full_name || 'Buddy'}
                      </p>
                    )}

                    <div style={{
                      backgroundColor: isSender ? '#FFB6C1' : '#2a2a2a',
                      color: isSender ? '#000' : '#e0e0e0',
                      padding: '0.875rem 1.25rem',
                      borderRadius: isSender 
                        ? '18px 6px 18px 18px' 
                        : '6px 18px 18px 18px',
                      border: isSender ? 'none' : '1px solid #404040',
                      boxShadow: isSender 
                        ? '0 2px 8px rgba(255, 182, 193, 0.2)' 
                        : '0 2px 8px rgba(0, 0, 0, 0.4)',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      animation: 'messageSlideIn 0.3s ease-out'
                    }}>
                      <p style={{ margin: 0, fontSize: '1rem', lineHeight: '1.4' }}>
                        {message.content}
                      </p>
                      <p style={{
                        margin: '0.5rem 0 0 0',
                        fontSize: '0.75rem',
                        opacity: isSender ? 0.6 : 0.5,
                        color: isSender ? '#333' : '#999',
                        textAlign: 'right'
                      }}>
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Sender Avatar (You) */}
                  {isSender && (
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: '#FFB6C1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1rem',
                      fontWeight: '700',
                      color: '#000',
                      flexShrink: 0
                    }}>
                      ~
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}

        {isTyping && (
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'center',
            color: '#999'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#FFB6C1',
              animation: 'bounce 1.4s infinite'
            }} />
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#FFB6C1',
              animation: 'bounce 1.4s infinite 0.2s'
            }} />
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#FFB6C1',
              animation: 'bounce 1.4s infinite 0.4s'
            }} />
            <span style={{ fontSize: '0.9rem' }}>typing...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        padding: '1.5rem 2rem',
        borderTop: '1px solid #333'
      }}>
        <div style={{
          display: 'flex',
          gap: '1rem',
          alignItems: 'flex-end'
        }}>
          <button
            style={{
              background: 'none',
              border: '1px solid #333',
              color: '#999',
              padding: '0.75rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1.1rem'
            }}
            title="Attach file"
          >
            <FontAwesomeIcon icon={faPaperclip} />
          </button>

          <textarea
            value={messageInput}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            rows={1}
            style={{
              flex: 1,
              padding: '0.75rem 1rem',
              backgroundColor: '#000',
              border: '1px solid #333',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '1rem',
              resize: 'none',
              maxHeight: '120px',
              overflow: 'auto',
              fontFamily: 'inherit'
            }}
          />

          <button
            onClick={handleSendMessage}
            disabled={sending || !messageInput.trim()}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: !messageInput.trim() ? '#666' : '#FFB6C1',
              border: 'none',
              borderRadius: '6px',
              color: !messageInput.trim() ? '#999' : '#000',
              fontWeight: '600',
              cursor: !messageInput.trim() ? 'not-allowed' : 'pointer',
              fontSize: '1rem'
            }}
          >
            <FontAwesomeIcon icon={faPaperPlane} /> Send
          </button>

          <button
            style={{
              background: 'none',
              border: '1px solid #333',
              color: '#999',
              padding: '0.75rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '1.1rem'
            }}
            title="Emoji"
          >
            <FontAwesomeIcon icon={faSmile} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% {
            opacity: 0.3;
            transform: translateY(0);
          }
          40% {
            opacity: 1;
            transform: translateY(-10px);
          }
        }
      `}</style>
    </div>
  );
};

export default BuddyChat;
